import React from 'react';
import { FormControlLabel, Radio, RadioGroup, Typography, Box } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../theme';

/**
 * ViewToggle - A reusable component for toggling between views
 *
 * Backwards compatible default rendering for two options: 'cards' and 'table'.
 * For more flexibility, pass an `items` array with objects: { value, label, icon }.
 *
 * @param {Object} props
 * @param {string} props.value - Current view value
 * @param {Function} props.onChange - Callback when view changes
 * @param {string} [props.cardsLabel] - Label for default 'cards' option
 * @param {string} [props.tableLabel] - Label for default 'table' option
 * @param {string} [props.label] - Group label
 * @param {Array<{value:string,label:string,icon?:React.ReactNode}>} [props.items] - Custom options
 */
const ViewToggle = ({ 
    value = 'cards', 
    onChange, 
    cardsLabel = 'Card', 
    tableLabel = 'Table',
    label = 'View:',
    items
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    // Handle view mode change
    const handleChange = (event) => {
        if (onChange) {
            onChange(event.target.value);
        }
    };
    
    const renderItems = Array.isArray(items) && items.length > 0
        ? items
        : [
            {
                value: 'cards',
                label: (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <GridViewIcon fontSize="small" />
                        <Typography variant="body2">{cardsLabel}</Typography>
                    </Box>
                )
            },
            {
                value: 'table',
                label: (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <TableRowsIcon fontSize="small" />
                        <Typography variant="body2">{tableLabel}</Typography>
                    </Box>
                )
            }
        ];

    return (
        <Box 
            display="flex" 
            alignItems="center" 
            gap={2} 
            sx={{ 
                borderRadius: '8px', 
                padding: '8px 16px',
                backgroundColor: "background.default",
                //boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
        >
            <Typography variant="body2" fontWeight="medium" sx={{ color: 'text.secondary' }}>
                {label}
            </Typography>
            
            <RadioGroup 
                row 
                value={value} 
                onChange={handleChange}
            >
                {renderItems.map((it, idx) => (
                    <FormControlLabel
                        key={it.value || idx}
                        value={it.value}
                        control={
                            <Radio 
                                size="small" 
                                sx={{ 
                                    color: colors.grey[400],
                                    '&.Mui-checked': {
                                        color: colors.primary[100]
                                    }
                                }}
                            />
                        }
                        label={it.label}
                        sx={{ marginRight: idx < renderItems.length - 1 ? 2 : 0 }}
                    />
                ))}
            </RadioGroup>
        </Box>
    );
};

export default ViewToggle;
